const api_domain = process.env.REACT_APP_API_DOMAIN;

// Type definitions
interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

export interface AuthError extends Error {
  status?: number;
  type?: string;
  error?: string;
}

export interface HttpError extends Error {
  status: number;
  body: string;
}
export interface ApiClient {
  get: (url: string, options?: RequestOptions) => Promise<Response | undefined>;
  post: (url: string, body?: BodyInit, options?: RequestOptions) => Promise<Response | undefined>;
  put: (url: string, body?: BodyInit, options?: RequestOptions) => Promise<Response | undefined>;
  delete: (url: string, options?: RequestOptions) => Promise<Response | undefined>;
}

type GetTokenFn = () => Promise<string>;
type HandleAuthErrorFn = <T>(retryFn: () => Promise<T>, error: AuthError) => Promise<T>;

export const createApiClient = (
  getToken: GetTokenFn,
  handleAuthError: HandleAuthErrorFn,
  domain: string = api_domain || ""
): ApiClient => {
  const request = async (endpoint: string, options: RequestOptions = {}): Promise<Response | undefined> => {
    const url = `${domain}${endpoint}`;
    const token = await getToken();
    const response = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        const error = new Error(`Authentication error: ${response.status}`) as AuthError;
        error.status = response.status;
        error.type = "auth";
        throw error;
      }

      const text = await response.text().catch(() => "");
      const error = new Error(`HTTP ${response.status}: ${text || response.statusText}`) as HttpError;
      error.status = response.status;
      error.body = text;
      throw error;
    }

    // Why returning undefined on 204 ???
    if (response.status === 204) return undefined;
    return response;
  };

  // Wrap requests so auth errors go through onAuthError for retries
  const withAuthRetry = <T>(makeRequest: () => Promise<T>): Promise<T> => {
    const attempt = async (): Promise<T> => {
      try {
        return await makeRequest();
      } catch (err) {
        const error = err as AuthError;
        // Token acquisition errors from getToken
        const isTokenError =
          error.error === "login_required" ||
          error.error === "consent_required" ||
          error.error === "interaction_required" ||
          error.error === "missing_refresh_token" ||
          error.error === "invalid_grant";

        // 401/403 from the API means the token is invalid server-side
        const isApiAuthError = error.type === "auth";

        if (!isTokenError && !isApiAuthError) {
          throw err;
        }

        // Delegate to onAuthError to handle reauth and retries
        return handleAuthError(attempt, error);
      }
    };

    // First attempt starts immediately. If auth error, onAuthError returns
    // a promise that resolve after reauth and retry.
    return attempt();
  };

  return {
    get: (url: string, options?: RequestOptions) => withAuthRetry(() => request(url, { ...options, method: "GET" })),
    post: (url: string, body?: BodyInit, options?: RequestOptions) =>
      withAuthRetry(() => request(url, { ...options, method: "POST", body })),
    put: (url: string, body?: BodyInit, options?: RequestOptions) =>
      withAuthRetry(() => request(url, { ...options, method: "PUT", body })),
    delete: (url: string, options?: RequestOptions) =>
      withAuthRetry(() => request(url, { ...options, method: "DELETE" })),
  };
};
